import React, { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BookmarkCheck, Cloud, ListOrdered, Route, Share2, type LucideIcon } from 'lucide-react';
import { authErrorTitle, isDeviceLimitError } from '../../api/authApi';
import { isAuthError, useAuth } from '../../auth/AuthContext';
import type { DeviceLimitChallenge, GoogleProfile } from '../../auth/types';
import { useRedirectIfAuthenticated } from '../../auth/useRedirectIfAuthenticated';
import AuthErrorBanner from '../../components/auth/AuthErrorBanner';
import AuthLoadingOverlay from '../../components/auth/AuthLoadingOverlay';
import DeviceLimitPanel from '../../components/auth/DeviceLimitPanel';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import { isGoogleSignInConfigured } from '../../config/google';
import ThemeToggle from '../../theme/ThemeToggle';
import ItineraryRoutePreview from './itinerary-route-preview';
import './login-page.css';
const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34A853" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

type PendingAction = 'login' | 'signup' | 'google' | null;

type AccountBenefit = {
  Icon: LucideIcon;
  tone: 'yellow' | 'violet' | 'blue' | 'orange' | 'green';
  title: string;
  desc: string;
};

const ACCOUNT_BENEFITS: AccountBenefit[] = [
  {
    Icon: BookmarkCheck,
    tone: 'violet',
    title: 'Saved locations',
    desc: 'Store favourite coordinates once and reuse them on any device.',
  },
  {
    Icon: ListOrdered,
    tone: 'orange',
    title: 'Stationary playlists',
    desc: 'Queue saved pins and cycle through them automatically with per-point timing.',
  },
  {
    Icon: Route,
    tone: 'blue',
    title: 'Routes & itineraries',
    desc: 'Simulate movement along custom routes with speed and stop controls.',
  },
  {
    Icon: Share2,
    tone: 'green',
    title: 'Share points',
    desc: 'Send saved locations to teammates or open links shared with you in one tap.',
  },
  {
    Icon: Cloud,
    tone: 'yellow',
    title: 'Cloud sync',
    desc: 'Your points and routes stay in sync wherever you sign in.',
  },
];

const LeftPanel: React.FC = () => (
  <section className="login-intro">
    <span className="login-left-badge">MockLoc account</span>
    <h1 className="login-left-title">
      One login.
      <span>All your mock data.</span>
    </h1>
    <p className="login-left-subtitle">
      Sign in to back up locations, build routes, and pick up testing on any Android device.
    </p>
    <ItineraryRoutePreview />
    <ul className="login-benefits">
      {ACCOUNT_BENEFITS.map(({ Icon, tone, title, desc }) => (
        <li key={title} className="login-benefit ml-tone" data-tone={tone}>
          <div className="login-benefit__icon">
            <Icon size={18} strokeWidth={2} aria-hidden="true" />
          </div>
          <div className="login-benefit__body">
            <h3>{title}</h3>
            <p>{desc}</p>
          </div>
        </li>
      ))}
    </ul>
  </section>
);

interface LoginFormProps {
  email: string;
  password: string;
  showPassword: boolean;
  disabled: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
  onSubmit: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  email,
  password,
  showPassword,
  disabled,
  onEmailChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
}) => (
  <form className="login-form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
    <label className="login-field">
      <span>Email or phone</span>
      <input
        type="text"
        className="input-field"
        placeholder="name@email.com"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        autoComplete="username"
        disabled={disabled}
        required
      />
    </label>
    <label className="login-field">
      <span>Password</span>
      <span className="input-wrapper">
        <input
          type={showPassword ? 'text' : 'password'}
          className="input-field"
          placeholder="Your password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="current-password"
          disabled={disabled}
          required
        />
        <button
          type="button"
          className="input-icon-btn"
          onClick={onTogglePassword}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </span>
    </label>
    <button type="submit" className="login-btn-submit" disabled={disabled}>
      Sign in
    </button>
  </form>
);

interface SignUpFormProps {
  username: string;
  email: string;
  companyName: string;
  whatsapp: string;
  password: string;
  isDeveloper: boolean;
  showPassword: boolean;
  disabled: boolean;
  onUsernameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onCompanyNameChange: (value: string) => void;
  onWhatsappChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onDeveloperChange: (value: boolean) => void;
  onTogglePassword: () => void;
  onSubmit: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  username,
  email,
  companyName,
  whatsapp,
  password,
  isDeveloper,
  showPassword,
  disabled,
  onUsernameChange,
  onEmailChange,
  onCompanyNameChange,
  onWhatsappChange,
  onPasswordChange,
  onDeveloperChange,
  onTogglePassword,
  onSubmit,
}) => (
  <form className="login-form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
    <label className="login-field">
      <span>Username</span>
      <input
        type="text"
        className="input-field"
        placeholder="Your name"
        value={username}
        onChange={(e) => onUsernameChange(e.target.value)}
        autoComplete="username"
        disabled={disabled}
        required
      />
    </label>
    <label className="login-field">
      <span>Email</span>
      <input
        type="email"
        className="input-field"
        placeholder="name@email.com"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        autoComplete="email"
        disabled={disabled}
        required
      />
    </label>
    <label className="login-field">
      <span>Company name</span>
      <input
        type="text"
        className="input-field"
        placeholder="Company or team"
        value={companyName}
        onChange={(e) => onCompanyNameChange(e.target.value)}
        autoComplete="organization"
        disabled={disabled}
        required
      />
    </label>
    <label className="login-field">
      <span>WhatsApp</span>
      <input
        type="text"
        className="input-field"
        placeholder="Optional"
        value={whatsapp}
        onChange={(e) => onWhatsappChange(e.target.value)}
        autoComplete="tel"
        disabled={disabled}
      />
    </label>
    <div className="whatsapp-helper">
      <WhatsAppIcon />
      <p>Optional: helps us reach you for support or product updates.</p>
    </div>
    <div className="dev-box">
      <div className="dev-box__title">Are you a developer?</div>
      <div className="dev-box__options">
        <label className="radio-label">
          <input
            type="radio"
            name="isDev"
            checked={isDeveloper}
            onChange={() => onDeveloperChange(true)}
            disabled={disabled}
          /> Yes
        </label>
        <label className="radio-label">
          <input
            type="radio"
            name="isDev"
            checked={!isDeveloper}
            onChange={() => onDeveloperChange(false)}
            disabled={disabled}
          /> No
        </label>
      </div>
    </div>
    <label className="login-field">
      <span>Password</span>
      <span className="input-wrapper">
        <input
          type={showPassword ? 'text' : 'password'}
          className="input-field"
          placeholder="6+ characters"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="new-password"
          disabled={disabled}
          minLength={6}
          required
        />
        <button
          type="button"
          className="input-icon-btn"
          onClick={onTogglePassword}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </span>
    </label>
    <button type="submit" className="login-btn-submit" disabled={disabled}>
      Create account
    </button>
  </form>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

interface GoogleAuthSectionProps {
  label: string;
  disabled: boolean;
  configured: boolean;
  onUnconfiguredClick: () => void;
  onGoogleStart: () => void;
  onGoogleProfile: (profile: GoogleProfile) => void;
  onGoogleError: (message: string) => void;
}

const GoogleAuthSection: React.FC<GoogleAuthSectionProps> = ({
  label,
  disabled,
  configured,
  onUnconfiguredClick,
  onGoogleStart,
  onGoogleProfile,
  onGoogleError,
}) => (
  <>
    {configured ? (
      <GoogleSignInButton
        label={label}
        disabled={disabled}
        onStart={onGoogleStart}
        onProfile={onGoogleProfile}
        onError={onGoogleError}
      />
    ) : (
      <button
        type="button"
        className="login-btn-google"
        onClick={onUnconfiguredClick}
        disabled={disabled}
      >
        <GoogleIcon /> {label}
      </button>
    )}
  </>
);

const LoginView: React.FC = () => {
  const navigate = useNavigate();
  const { login, signup, loginWithGoogle, completeDeviceLogin } = useAuth();
  useRedirectIfAuthenticated();

  const googleConfigured = isGoogleSignInConfigured();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLogin, setIsLogin] = useState(() => searchParams.get('mode') !== 'signup');
  const [loading, setLoading] = useState(false);
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [lastGoogleProfile, setLastGoogleProfile] = useState<GoogleProfile | null>(null);
  const [deviceLimit, setDeviceLimit] = useState<DeviceLimitChallenge | null>(null);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [signupUsername, setSignupUsername] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupCompany, setSignupCompany] = useState('');
  const [signupWhatsapp, setSignupWhatsapp] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupIsDeveloper, setSignupIsDeveloper] = useState(true);
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  const clearError = useCallback(() => {
    setErrorTitle(null);
    setErrorMessage(null);
    setDeviceLimit(null);
  }, []);

  const showError = useCallback(
    (title: string, message: string, action: PendingAction) => {
      setErrorTitle(title);
      setErrorMessage(message);
      setPendingAction(action);
    },
    []
  );

  const showAuthFailure = useCallback(
    (error: unknown, action: PendingAction) => {
      if (isAuthError(error)) {
        const context =
          action === 'signup' ? 'signup' : action === 'google' ? 'google' : 'login';
        showError(authErrorTitle(error.kind, context), error.message, action);
        return;
      }
      showError(
        'Something went wrong',
        'Please try again in a moment.',
        action
      );
    },
    [showError]
  );

  const switchTab = useCallback((loginTab: boolean) => {
    setIsLogin(loginTab);
    clearError();
    const next = new URLSearchParams(searchParams);
    if (loginTab) {
      next.delete('mode');
    } else {
      next.set('mode', 'signup');
    }
    setSearchParams(next, { replace: true });
  }, [clearError, searchParams, setSearchParams]);

  const handleLogin = useCallback(async () => {
    if (!loginEmail.trim() || !loginPassword) {
      showError('Missing details', 'Email and password are required.', 'login');
      return;
    }

    setLoading(true);
    clearError();
    try {
      await login(loginEmail, loginPassword);
      navigate('/app', { replace: true });
    } catch (error) {
      if (isDeviceLimitError(error) && error.details) {
        setDeviceLimit(error.details);
        return;
      }
      showAuthFailure(error, 'login');
    } finally {
      setLoading(false);
    }
  }, [login, loginEmail, loginPassword, navigate, clearError, showError, showAuthFailure]);

  const validateSignup = useCallback((): string | null => {
    if (!signupUsername.trim()) return 'Username is required.';
    if (!signupEmail.trim() || !signupEmail.includes('@')) {
      return 'Please enter a valid email address.';
    }
    if (!signupCompany.trim()) return 'Company name is required.';
    if (signupPassword.length < 6) {
      return 'Password must be at least 6 characters.';
    }
    return null;
  }, [signupUsername, signupEmail, signupCompany, signupPassword]);

  const handleSignup = useCallback(async () => {
    const validationError = validateSignup();
    if (validationError) {
      showError('Check your details', validationError, 'signup');
      return;
    }

    setLoading(true);
    clearError();
    try {
      await signup({
        userName: signupUsername,
        userEmail: signupEmail,
        userPhone: signupWhatsapp,
        password: signupPassword,
        isDeveloper: signupIsDeveloper,
      });
      navigate('/app', { replace: true });
    } catch (error) {
      showAuthFailure(error, 'signup');
    } finally {
      setLoading(false);
    }
  }, [
    signup,
    signupUsername,
    signupEmail,
    signupWhatsapp,
    signupPassword,
    signupIsDeveloper,
    navigate,
    validateSignup,
    clearError,
    showError,
    showAuthFailure,
  ]);

  const completeGoogleAuth = useCallback(
    async (profile: GoogleProfile) => {
      setLastGoogleProfile(profile);
      setLoading(true);
      clearError();
      try {
        await loginWithGoogle(profile, signupIsDeveloper);
        navigate('/app', { replace: true });
      } catch (error) {
        showAuthFailure(error, 'google');
      } finally {
        setLoading(false);
      }
    },
    [loginWithGoogle, signupIsDeveloper, navigate, clearError, showAuthFailure]
  );

  const handleGoogleProfile = useCallback(
    (profile: GoogleProfile) => {
      void completeGoogleAuth(profile);
    },
    [completeGoogleAuth]
  );

  const handleGoogleStart = useCallback(() => {
    clearError();
    setPendingAction('google');
  }, [clearError]);

  const handleGoogleError = useCallback(
    (message: string) => {
      setLastGoogleProfile(null);
      showError('Google sign-in failed', message, 'google');
    },
    [showError]
  );

  const handleGoogleUnconfigured = useCallback(() => {
    showError(
      'Google sign-in not configured',
      'Add your Google OAuth client ID to REACT_APP_GOOGLE_CLIENT_ID in .env.development (local) or .env.production (deploy), then restart the app.',
      'google'
    );
  }, [showError]);

  const handleReplaceDevice = useCallback(
    async (sessionId: string) => {
      if (!deviceLimit) return;
      setLoading(true);
      setPendingAction('login');
      setErrorTitle(null);
      setErrorMessage(null);
      try {
        await completeDeviceLogin(deviceLimit.claimToken, sessionId);
        navigate('/app', { replace: true });
      } catch (error) {
        setDeviceLimit(null);
        showAuthFailure(error, 'login');
      } finally {
        setLoading(false);
      }
    },
    [deviceLimit, completeDeviceLogin, navigate, showAuthFailure]
  );

  const handleRetry = useCallback(() => {
    if (pendingAction === 'login') {
      void handleLogin();
      return;
    }
    if (pendingAction === 'signup') {
      void handleSignup();
      return;
    }
    if (pendingAction === 'google') {
      if (lastGoogleProfile) {
        void completeGoogleAuth(lastGoogleProfile);
      }
    }
  }, [pendingAction, handleLogin, handleSignup, lastGoogleProfile, completeGoogleAuth]);

  const loadingTitle =
    pendingAction === 'google'
      ? 'Signing in with Google…'
      : isLogin
        ? 'Signing you in…'
        : 'Creating your account…';
  const loadingSubtitle =
    pendingAction === 'google'
      ? 'Connecting your Google account to MockLoc.'
      : isLogin
        ? 'Fetching your account details securely.'
        : 'Setting up your MockLoc account.';

  return (
    <div className="login-root">
      <header className="login-top">
        <Link to="/" className="login-brand">
          <img src="/mockloc-icon.png" alt="" className="login-brand__logo" />
          <span>MockLoc</span>
        </Link>
        <div className="login-top__actions">
          <ThemeToggle />
          <Link to="/" className="login-home">
            <BackIcon />
            Home
          </Link>
        </div>
      </header>

      <div className="login-body">
      <LeftPanel />

      <div className="login-panel">
        <div className="login-card">
          <div className="login-header">
            <h2 className="login-title">{isLogin ? 'Welcome back' : 'Create your account'}</h2>
            <p className="login-subtitle">
              {isLogin ? 'Sign in to pick up where you left off.' : 'A few details and you are ready to go.'}
            </p>
          </div>

          {deviceLimit && (
            <DeviceLimitPanel
              key={deviceLimit.claimToken}
              message={deviceLimit.message}
              devices={deviceLimit.devices}
              disabled={loading}
              onContinue={(sessionId) => void handleReplaceDevice(sessionId)}
              onDismiss={clearError}
            />
          )}

          {errorMessage && (
            <AuthErrorBanner
              title={errorTitle ?? undefined}
              message={errorMessage}
              onRetry={
                pendingAction && (pendingAction !== 'google' || lastGoogleProfile)
                  ? handleRetry
                  : undefined
              }
              onDismiss={clearError}
            />
          )}

          <div className="login-tabs" role="tablist" aria-label="Account">
            <button
              type="button"
              role="tab"
              id="login-tab"
              className={`login-tab ${isLogin ? 'login-tab--active' : ''}`}
              aria-selected={isLogin}
              disabled={loading}
              onClick={() => switchTab(true)}
            >
              Log in
            </button>
            <button
              type="button"
              role="tab"
              id="signup-tab"
              className={`login-tab ${!isLogin ? 'login-tab--active' : ''}`}
              aria-selected={!isLogin}
              disabled={loading}
              onClick={() => switchTab(false)}
            >
              Sign up
            </button>
          </div>

          {isLogin ? (
            <>
              <GoogleAuthSection
                label="Sign in with Google"
                disabled={loading}
                configured={googleConfigured}
                onUnconfiguredClick={handleGoogleUnconfigured}
                onGoogleStart={handleGoogleStart}
                onGoogleProfile={handleGoogleProfile}
                onGoogleError={handleGoogleError}
              />
              <div className="login-divider">or continue with email</div>
              <LoginForm
                email={loginEmail}
                password={loginPassword}
                showPassword={showLoginPassword}
                disabled={loading}
                onEmailChange={setLoginEmail}
                onPasswordChange={setLoginPassword}
                onTogglePassword={() => setShowLoginPassword((v) => !v)}
                onSubmit={() => void handleLogin()}
              />
            </>
          ) : (
            <>
              <GoogleAuthSection
                label="Sign up with Google"
                disabled={loading}
                configured={googleConfigured}
                onUnconfiguredClick={handleGoogleUnconfigured}
                onGoogleStart={handleGoogleStart}
                onGoogleProfile={handleGoogleProfile}
                onGoogleError={handleGoogleError}
              />
              <div className="login-divider">or create with email</div>
              <SignUpForm
                username={signupUsername}
                email={signupEmail}
                companyName={signupCompany}
                whatsapp={signupWhatsapp}
                password={signupPassword}
                isDeveloper={signupIsDeveloper}
                showPassword={showSignupPassword}
                disabled={loading}
                onUsernameChange={setSignupUsername}
                onEmailChange={setSignupEmail}
                onCompanyNameChange={setSignupCompany}
                onWhatsappChange={setSignupWhatsapp}
                onPasswordChange={setSignupPassword}
                onDeveloperChange={setSignupIsDeveloper}
                onTogglePassword={() => setShowSignupPassword((v) => !v)}
                onSubmit={() => void handleSignup()}
              />
            </>
          )}
        </div>
      </div>
      </div>

      {loading && (
        <AuthLoadingOverlay title={loadingTitle} subtitle={loadingSubtitle} />
      )}
    </div>
  );
};

export default LoginView;
